// ServiceOS/frontend/src/components/forms/StockItemsField.js

import React, { useState, useMemo, useCallback } from 'react';
import ModernSelect from '../common/ModernSelect';
import ModernInput from '../common/ModernInput';
import { PlusIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'react-toastify';

const StockItemsField = ({ field, formData, setFormData, allStockItems, commonInputStyles, currentFieldLabelStyle, isFieldActuallyRequired }) => {
    const { formatCurrency } = useCurrency();
    
    // ⭐ FIX: State for the stock selector is now local to this component ⭐
    const [selectedStockId, setSelectedStockId] = useState('');
    const [quantity, setQuantity] = useState(1);

    const itemsToInvoice = useMemo(() => formData[field.name] || [], [formData, field.name]);

    const availableStockOptions = useMemo(() => {
        const selectedIds = new Set(itemsToInvoice.map(item => item.stockId));
        return [
            { value: '', label: 'Select a Stock Item...' },
            ...allStockItems
                .filter(item => !selectedIds.has(item._id))
                .map(item => ({ value: item._id, label: `${item.name} (Avail: ${item.stockQuantity})` }))
        ];
    }, [allStockItems, itemsToInvoice]);

    const handleAddStockItemClick = useCallback(() => {
        const stockItem = allStockItems.find(item => item._id === selectedStockId);
        if (!stockItem || quantity <= 0 || quantity > stockItem.stockQuantity) {
            toast.error('Invalid selection or quantity.');
            return;
        }

        const newStockItem = { 
            stockId: stockItem._id, 
            name: stockItem.name, 
            quantity: quantity, 
            salePrice: stockItem.salePrice 
        };

        setFormData(prev => ({
            ...prev,
            [field.name]: [...itemsToInvoice, newStockItem]
        }));
        
        setSelectedStockId('');
        setQuantity(1);
    }, [selectedStockId, quantity, allStockItems, itemsToInvoice, field.name, setFormData]);

    const handleRemoveStockItem = useCallback((stockId) => {
        setFormData(prev => ({
            ...prev,
            [field.name]: itemsToInvoice.filter(item => item.stockId !== stockId)
        }));
    }, [itemsToInvoice, field.name, setFormData]);

    return (
        <div key={field.id} className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {field.label}{isFieldActuallyRequired ? ' *' : ''}
            </label>
            <div className="flex items-end gap-2">
                <div className="flex-grow">
                    <ModernSelect
                        label="Stock Item"
                        value={selectedStockId}
                        onChange={(e) => setSelectedStockId(e.target.value)}
                        options={availableStockOptions}
                    />
                </div>
                <ModernInput
                    label="Qty"
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 0)}
                    min="1"
                    className="w-20"
                />
                <button type="button" onClick={handleAddStockItemClick} className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 h-10">
                    <PlusIcon className="h-5 w-5" />
                </button>
            </div>
            <div className="mt-4 border dark:border-gray-600 rounded-lg p-2 space-y-2 min-h-[100px]">
                {itemsToInvoice.length > 0 ? itemsToInvoice.map(item => (
                    <div key={item.stockId} className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/30 p-2 rounded">
                        <span className="text-gray-800 dark:text-gray-200">{item.name} (Qty: {item.quantity})</span>
                        <div className="flex items-center gap-4">
                            <span className="text-gray-800 dark:text-gray-200">{formatCurrency(item.salePrice * item.quantity)}</span>
                            <button onClick={() => handleRemoveStockItem(item.stockId)} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
                                <XCircleIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                )) : <p className="text-center text-gray-500 dark:text-gray-400 p-4">No items added yet.</p>}
            </div>
            {itemsToInvoice.length > 0 && (
                <div className="text-right font-bold text-xl mt-2 text-gray-800 dark:text-white">
                    Subtotal: {formatCurrency(itemsToInvoice.reduce((sum, item) => sum + (item.salePrice * item.quantity), 0))}
                </div>
            )}
        </div>
    );
};

export default StockItemsField;
